import FlowDetailsForm from '@/app/FlowDetailsForm';
import { GuideEditBaseContext } from '@/app/guide/guideEditBase';
import { draftFlowsState, selectedFlowIdState } from '@/state';
import { guideContextForParams, liveGuideFlowState, previewGuideFlowIdState } from '@/state/guide.state';
import { useLoadableAtomValue, useUnwrappedAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import type { SpaceId } from '@rock/types';
import { useAtom } from 'jotai';
import * as React from 'react';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import type { AppFlowParams } from '@rock/types';

const log = logger('FlowInitializer');

export const FlowParamsContext = React.createContext<AppFlowParams>({
    spaceId: '' as SpaceId,
    equipmentTypeId: undefined,
});

const FlowInitializer: FC<PropsWithChildren<{ params: AppFlowParams; flowId?: string; editBase?: string }>> = ({
    params,
    flowId,
    editBase,
    children,
}) => {
    const [selectedFlowId, setSelectedFlowId] = useAtom(selectedFlowIdState(params));

    const draftFlows = useUnwrappedAtomValue(draftFlowsState(params)) ?? [];
    const currentFlow = draftFlows.find((f) => f.id === selectedFlowId && f.space === params.spaceId);
    const [liveFlow, liveFlowLoadable] = useLoadableAtomValue(liveGuideFlowState(params));
    const liveFlowReady = liveFlowLoadable.state !== 'loading';
    const [previewFlowId, previewFlowLoadable] = useLoadableAtomValue(
        previewGuideFlowIdState(guideContextForParams(params)),
    );
    const previewFlowReady = previewFlowLoadable.state !== 'loading';

    // A guide's URL names the flow to open, but only ONCE per id. Selection is atom state the editor
    // writes as you work — the guide dropdown and the create-guide dialog both set it — so an effect
    // that re-asserted the URL on every run would snap those straight back. A stale id applies
    // nothing and falls through to the ladder below rather than leaving the canvas blank.
    const appliedFlowId = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (flowId && appliedFlowId.current !== flowId && draftFlows.some((flow) => flow.id === flowId)) {
            appliedFlowId.current = flowId;
            setSelectedFlowId(flowId);
            return;
        }
        if (!currentFlow && draftFlows.length > 0 && liveFlowReady && previewFlowReady) {
            const publishedFlow = liveFlow ? draftFlows.find((flow) => flow.id === liveFlow.id) : undefined;
            // Nothing published yet, but a flow may be staged in preview — auto-show that one.
            const previewFlow = previewFlowId ? draftFlows.find((flow) => flow.id === previewFlowId) : undefined;
            if (publishedFlow) {
                log.info(`Selecting published flow for space: ${params.spaceId}`);
                setSelectedFlowId(publishedFlow.id);
            } else if (previewFlow) {
                log.info(`Selecting preview flow for space: ${params.spaceId}`);
                setSelectedFlowId(previewFlow.id);
            } else {
                log.info(`Selecting first draft flow for space: ${params.spaceId}`);
                setSelectedFlowId(draftFlows[0]!.id);
            }
        }
    }, [
        flowId,
        currentFlow,
        params.spaceId,
        draftFlows,
        liveFlow,
        liveFlowReady,
        previewFlowId,
        previewFlowReady,
        setSelectedFlowId,
    ]);

    return (
        <FlowParamsContext.Provider value={params}>
            <GuideEditBaseContext.Provider value={editBase}>
                {children}
                <FlowDetailsForm params={params} />
            </GuideEditBaseContext.Provider>
        </FlowParamsContext.Provider>
    );
};

export default FlowInitializer;
